import React from 'react';
// Character limits based on database schema
const APP_CHAR_LIMIT = 200;
const APPLICATION_CHAR_LIMIT = 200;
import { Card, Title, Button } from '@tremor/react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import type { EOIResponse } from '../../types/eoi-response';
import type { Applicant } from '../../types/applicant';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Partial<EOIResponse>;
  onSubmit: (data: Omit<EOIResponse, 'reoi_id'>) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  title: string;
  submitLabel: string;
  isEditMode?: boolean;
}

export default function EOIResponseFormModal({
  isOpen,
  onClose,
  initialData,
  onSubmit,
  isSubmitting,
  error,
  title,
  submitLabel,
  isEditMode = false
}: Props) {
  // Add a state to track the selected applicant ID (for the dropdown)
  const [selectedApplicantId, setSelectedApplicantId] = React.useState<string>(initialData?.reoi_app || '');
  
  const [formData, setFormData] = React.useState<Omit<EOIResponse, 'reoi_id'>>({
    reoi_eoi: initialData?.reoi_eoi || '',
    reoi_app: initialData?.reoi_app || '', // This will now store the applicant name
    reoi_application: initialData?.reoi_application || '',
    reoi_date: initialData?.reoi_date || new Date().toISOString().split('T')[0],
    reoi_success: initialData?.reoi_success || ''
  });

  // For debugging - log initial data to help troubleshoot
  React.useEffect(() => {
    if (isEditMode && initialData) {
      console.log('Edit mode initialData:', initialData);
    }
  }, [isEditMode, initialData]);

  // Track character counts
  const appCharCount = formData.reoi_app.length;
  const applicationCharCount = formData.reoi_application.length;

  // Fetch lease EOIs for the dropdown
  const { data: leaseEOIs, isLoading: isLoadingEOIs } = useQuery({
    queryKey: ['lease-eois'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lease_eois')
        .select('eoi_id, eoi_lot')
        .order('eoi_id', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  // Fetch applicants for the dropdown
  const { data: applicants, isLoading: isLoadingApplicants } = useQuery({
    queryKey: ['applicants'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('applicants')
        .select('appl_id, appl_name')
        .order('appl_name');

      if (error) throw error;
      return data;
    }
  });
  
  // Updated useEffect to handle applicant loading and mapping
  React.useEffect(() => {
    if (initialData && applicants && !isLoadingApplicants) {
      console.log('Setting formData from initialData with applicants loaded:', initialData);
      
      // First, check if initialData.reoi_app is an ID by checking if it's numeric
      const isNumericId = /^\d+$/.test(initialData.reoi_app || '');
      let applicantId = '';
      let applicantName = initialData.reoi_app || '';
      
      if (isNumericId) {
        // If it's an ID, find the applicant name for it
        const applicant = applicants.find(a => a.appl_id.toString() === initialData.reoi_app);
        if (applicant) {
          applicantId = initialData.reoi_app || '';
          applicantName = applicant.appl_name;
          console.log(`Found applicant name "${applicantName}" for ID ${applicantId}`);
        }
      } else if (initialData.reoi_app) {
        // If it's a name, find the corresponding ID
        const applicant = applicants.find(a => a.appl_name === initialData.reoi_app);
        if (applicant) {
          applicantId = applicant.appl_id.toString();
          console.log(`Found applicant ID ${applicantId} for name "${applicantName}"`);
        }
      }
      
      // Update both the form data and the selected ID
      setFormData({
        reoi_eoi: initialData.reoi_eoi || '',
        reoi_app: applicantName, // Always store the name now
        reoi_application: initialData.reoi_application || '',
        reoi_date: initialData.reoi_date || new Date().toISOString().split('T')[0],
        reoi_success: initialData.reoi_success || ''
      });
      
      setSelectedApplicantId(applicantId);
    } else if (initialData) {
      // If applicants aren't loaded yet, just set the form data
      console.log('Setting initial formData while waiting for applicants:', initialData);
      setFormData({
        reoi_eoi: initialData.reoi_eoi || '',
        reoi_app: initialData.reoi_app || '',
        reoi_application: initialData.reoi_application || '',
        reoi_date: initialData.reoi_date || new Date().toISOString().split('T')[0],
        reoi_success: initialData.reoi_success || ''
      });
    }
  }, [initialData, applicants, isLoadingApplicants]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Add debugging to see the final form data
    console.log('Submitting EOI Response with applicant name:', formData.reoi_app);
    console.log('Full form data:', formData);
    
    await onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <div className="flex justify-between items-center mb-6">
          <Title>{title}</Title>
          <Button variant="light" onClick={onClose}>Close</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                EOI Reference *
              </label>
              {isEditMode ? (
                <>
                  <div className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
                    {leaseEOIs && !isLoadingEOIs
                      ? leaseEOIs.find(eoi => eoi.eoi_id.toString() === formData.reoi_eoi)
                          ? `${formData.reoi_eoi} - ${leaseEOIs.find(eoi => eoi.eoi_id.toString() === formData.reoi_eoi)?.eoi_lot}`
                          : formData.reoi_eoi
                      : 'Loading...'}
                  </div>
                  <input type="hidden" value={formData.reoi_eoi} name="reoi_eoi" />
                </>
              ) : (
                <select
                  value={formData.reoi_eoi}
                  onChange={(e) => setFormData(prev => ({ ...prev, reoi_eoi: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  required
                >
                  <option value="">Select EOI Reference</option>
                  {isLoadingEOIs ? (
                    <option value="" disabled>Loading...</option>
                  ) : (
                    leaseEOIs?.map((eoi) => (
                      <option key={eoi.eoi_id} value={eoi.eoi_id.toString()}>
                        {eoi.eoi_id} - {eoi.eoi_lot}
                      </option>
                    ))
                  )}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {isEditMode ? 'Expression of Interest this response is for' : 'Select the Expression of Interest this response is for'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                EoI Applicant *
              </label>
              {isEditMode ? (
                <>
                  {/* Display current applicant in a non-editable field with dropdown functionality */}
                  <div className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 flex justify-between items-center">
                    <span>{formData.reoi_app || 'Select Applicant'}</span>
                    <button 
                      type="button"
                      className="text-gray-500 hover:text-primary"
                      onClick={() => {
                        // Use a simple dropdown rather than a hidden div with a select
                        const select = document.getElementById('applicant-select') as HTMLSelectElement;
                        if (select) {
                          select.style.display = 'block';
                          select.focus();
                          // Position the selector under the display field
                          select.style.position = 'absolute';
                          select.style.width = '100%';
                          select.style.zIndex = '10';
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Hidden dropdown that's positioned absolute */}
                  <div className="relative">
                    <select
                      id="applicant-select"
                      value={selectedApplicantId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        setSelectedApplicantId(selectedId);
                        
                        if (selectedId && applicants) {
                          const applicant = applicants.find(a => a.appl_id.toString() === selectedId);
                          if (applicant) {
                            setFormData(prev => ({ 
                              ...prev, 
                              reoi_app: applicant.appl_name 
                            }));
                          }
                        } else {
                          setFormData(prev => ({ ...prev, reoi_app: '' }));
                        }
                        
                        // Hide the select after making a selection
                        const select = e.target as HTMLSelectElement;
                        select.style.display = 'none';
                      }}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                      required
                      style={{ display: 'none' }}
                      onBlur={(e) => {
                        // Hide when focus is lost
                        e.currentTarget.style.display = 'none';
                      }}
                    >
                      <option value="">Select Applicant</option>
                      {isLoadingApplicants ? (
                        <option value="" disabled>Loading applicants...</option>
                      ) : (
                        applicants?.map((applicant) => (
                          <option key={applicant.appl_id} value={applicant.appl_id.toString()}>
                            {applicant.appl_name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </>
              ) : (
                <select
                  value={selectedApplicantId}
                  onChange={(e) => {
                    setSelectedApplicantId(e.target.value);
                    // When an applicant is selected, find their name and store it in reoi_app
                    const selectedId = e.target.value;
                    if (selectedId && applicants) {
                      const applicant = applicants.find(a => a.appl_id.toString() === selectedId);
                      if (applicant) {
                        setFormData(prev => ({ 
                          ...prev, 
                          reoi_app: applicant.appl_name // Store name instead of ID
                        }));
                      }
                    } else {
                      setFormData(prev => ({ ...prev, reoi_app: '' }));
                    }
                  }}
                  className={`mt-1 block w-full rounded-md border ${selectedApplicantId && isEditMode ? 'border-primary' : 'border-gray-300'} px-3 py-2 focus:border-primary focus:ring-primary`}
                  required
                >
                  <option value="">Select Applicant</option>
                  {isLoadingApplicants ? (
                    <option value="" disabled>Loading applicants...</option>
                  ) : (
                    applicants?.map((applicant) => (
                      <option key={applicant.appl_id} value={applicant.appl_id.toString()}>
                        {applicant.appl_name}
                      </option>
                    ))
                  )}
                </select>
              )}
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-gray-500">
                  {selectedApplicantId ? 
                    isLoadingApplicants ? 
                      "Loading applicant details..." : 
                      "Selected applicant: " + (formData.reoi_app || "Unknown") : 
                    "Select the applicant who submitted this response"}
                </p>
                <a 
                  href="/admin/applicants" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  Manage Applicants
                </a>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Application Details *
              </label>
              <input
                type="text"
                value={formData.reoi_application}
                onChange={(e) => setFormData(prev => ({ ...prev, reoi_application: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                maxLength={APPLICATION_CHAR_LIMIT}
                required
              />
              <div className="flex justify-between">
                <div className="mt-1">
                  <p className="text-xs text-gray-500">
                    Enter detailed information about the application (max {APPLICATION_CHAR_LIMIT} characters)
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    Add URL to the document repository as needed
                  </p>
                </div>
                <p className={`mt-1 text-xs ${applicationCharCount >= APPLICATION_CHAR_LIMIT * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
                  {applicationCharCount}/{APPLICATION_CHAR_LIMIT}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date *
              </label>
              <input
                type="date"
                value={formData.reoi_date.split('T')[0]}
                onChange={(e) => setFormData(prev => ({ ...prev, reoi_date: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Success Status
              </label>
              <select
                value={formData.reoi_success}
                onChange={(e) => setFormData(prev => ({ ...prev, reoi_success: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              >
                <option value="P">Not Yet Resolved</option>
                <option value="Y">Successful (Y)</option>
                <option value="N">Unsuccessful (N)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Set to "Not Yet Resolved" until a decision has been made
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : submitLabel}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
